import { useGetProrations } from 'hooks/useGetProrations';
import { useUpdateEntry } from 'hooks/ledger/mutations';
import { useDeleteProration, useUpdateProration } from 'hooks/prorations/mutations';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { OrderProperty } from 'types/order';
import useAlert from 'utils/alert';
import { asyncForEach } from 'utils/helpers';

import { ModalData, ProrationsForm } from './types';
import { getPropertyProrationForms } from './utils';
import { useAccountingUpdateSocketEvent } from "hooks/useAccountingUpdateFromSocket";

type Params = {
	properties: OrderProperty[];
};

export const useTaxesAndProrations = ({ properties }: Params) => {
  const { id: orderId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [selectedProperty, setSelectedProperty] = useState<OrderProperty>();
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const prorationsForm = useForm<ProrationsForm>({
    defaultValues: { forms: [] },
    mode: 'onChange',
  });
  const [modalData, setModalData] = useState<ModalData>({ modal: '' });
  const [isUploading, setIsUploading] = useState(false);
  const showAlert = useAlert();
  const [pendingProrationId, setPendingProrationId] = useState<string>();
  const [prorationToDelete, setProrationToDelete] = useState<string>();

  useAccountingUpdateSocketEvent({
    callback: () => queryClient.invalidateQueries([{ orderId }, 'prorations'])
  });

  const {
    data: prorationsResponse,
    isLoading: isProrationsLoading,
    isFetching: isProrationsFetching,
  } = useGetProrations({
    orderId,
    queryConfig: {
      onSuccess: (response) => {
        const forms = selectedProperty && response.getOrder.prorations
          ? getPropertyProrationForms(
            selectedProperty._id,
            response.getOrder.prorations
          )
          : [];
        prorationsForm.reset({ forms });
      },
      staleTime: 50 * (60 * 1000),
    },
  });

  useEffect(() => {
    if (pendingProrationId && selectedProperty && prorationsResponse) {
      const forms = getPropertyProrationForms(
        selectedProperty._id,
        prorationsResponse.getOrder.prorations
      );
      const newProrationIndex = forms.findIndex(
        (form) => form._id === pendingProrationId
      );
      if (newProrationIndex !== -1) {
        setSelectedTabIndex(newProrationIndex);
        setPendingProrationId(undefined);
      }
    }
  }, [pendingProrationId, prorationsResponse, selectedProperty]);

  useEffect(() => {
    if (isProrationsLoading || isProrationsFetching) {
      showAlert('Getting new information', 'info');
    }
  }, [isProrationsLoading, isProrationsFetching, showAlert]);

  const selectProperty = (propertyId: string) => {
    const property = properties.find((p) => p._id === propertyId);
    if (property && prorationsResponse) {
      const forms = getPropertyProrationForms(property._id, prorationsResponse.getOrder.prorations);

      prorationsForm.reset({
        forms,
      });
    }
    setSelectedProperty(property);
  };

  useEffect(() => {
    setSelectedTabIndex(0);
    prorationsForm.reset({ forms: [] });

    if (properties.length > 0) {
      selectProperty(properties[0]._id);
    } else {
      setSelectedProperty(undefined);
    }
    // prorationsForm/selectProperty are unstable; re-running would reset the form on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties]);

  const { mutate: updateProrationMutation } = useUpdateProration({
    queryConfig: {
      onSuccess: (_, { prorationId }) => {
        const proration = prorationsForm
          .getValues('forms')
          .find((form) => form._id === prorationId);
        if (proration) {
          showAlert(`${proration.description} was successfully updated`, 'success');
        }
      },
    },
  });

  const { mutate: updateEntry } = useUpdateEntry({});

  const { mutate: deleteProration, isLoading: isDeleteLoading } = useDeleteProration({
    queryConfig: {
      onSuccess: () => {
        setSelectedTabIndex(0);
        setProrationToDelete(undefined);
        showAlert(`The proration was successfully deleted`, 'success');
      },
    },
  });

  const onSave = prorationsForm.handleSubmit(async (data) => {
    setIsUploading(true);
    const forms = prorationsForm.formState.dirtyFields.forms ?? [];

    const errors: string[] = [];
    await asyncForEach(forms, async (formFlags, formIndex) => {
      if (formFlags) {
        const formData = data.forms[formIndex!];
        const {
          _id,
          accounting,
          estate,
          days,
          description,
          anualAmount,
          perDiemAmount,
          proratedAmount,
          days_year,
          ...rest
        } = formData;

        if (formFlags.anualAmount) {
          await updateEntry(
            {
              id: orderId,
              accountingId: formData.anualAmount!._id,
              data: { amount_override: formData.anualAmount!.amount },
            },
            {
              onError: () => {
                errors.push(`Unexpected error while trying to update the amount of ${description}`);
              },
            }
          );
        }

        if (formFlags.proratedAmount) {
          await updateEntry(
            {
              id: orderId,
              accountingId: formData.proratedAmount!._id,
              data: { amount_override: formData.proratedAmount!.amount },
            },
            {
              onError: () => {
                errors.push(
                  `Unexpected error while trying to update the prorated amount of ${description}`
                );
              },
            }
          );
        }

        if (
          Object.keys(formFlags).filter(
            (key) => key !== 'anualAmount' && key !== 'perDiemAmount' && key !== 'proratedAmount'
          ).length > 0
        ) {
          await updateProrationMutation(
            {
              orderId,
              prorationId: formData._id,
              data: {
                ...rest,
              },
            },
            {
              onError: () => {
                errors.push(`Unexpected error while trying to update the ${description} proration`);
              },
            }
          );
        }
      }
    });

    if (errors.length > 0) {
      errors.forEach((error) => showAlert(error, 'error', 10000));
    } else {
      queryClient.invalidateQueries([{ orderId }, 'prorations']);
    }
    setIsUploading(false);
  });

  const onDelete = (prorationId: string) => setProrationToDelete(prorationId);

  const onConfirmDelete = () => {
    if (!prorationToDelete) {
      return;
    }

    deleteProration({ orderId, prorationId: prorationToDelete });
  };

  const onCreated = (proratedId: string) => {
    setPendingProrationId(proratedId);
  };

  const isLoading = isUploading || isDeleteLoading || isProrationsLoading;
  return {
    selectedProperty,
    setSelectedProperty,
    selectProperty,
    prorationsForm,
    selectedTabIndex,
    setSelectedTabIndex,
    modalData,
    setModalData,
    onSave,
    onDelete,
    onConfirmDelete,
    onCancelDelete: () => setProrationToDelete(undefined),
    isDeleteLoading,
    isDeleteDialogOpen: Boolean(prorationToDelete),
    onCreated,
    isLoading,
  };
};
